import { DustExZone, DustExZoneType } from "@domain/project/floorplan/zones/dust-ex-zone";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { ZoneKonva } from "@project/floorplanner/konva/zones/zone-konva";
import Konva from "konva";
import { Group } from "konva/lib/Group";
import { Rect } from "konva/lib/shapes/Rect";

export class DustExZoneKonva {
  static init(dustExZone: DustExZone): Group {
    let color: string = "";
    let dotColorRGB: number[] = [];
    switch (dustExZone.dustExZoneType) {
      case DustExZoneType.ZONE_20:
        color = "red";
        dotColorRGB = [255, 0, 0];
        break;
      case DustExZoneType.ZONE_21:
        color = "orange";
        dotColorRGB = [255, 165, 0];

        break;
      case DustExZoneType.ZONE_22:
        color = "yellow";
        dotColorRGB = [255, 227, 0];
        break;
    }

    const parentGroup = new Group();

    const dustExZoneGroup = new Group({
      x: dustExZone.x,
      y: dustExZone.y,
      height: dustExZone.height,
      width: dustExZone.width,
      rotation: dustExZone.rotation,
      draggable: false,
    });

    const dotPattern = new Image();
    dotPattern.onload = () => {
      let dotPatternImage = new Konva.Image({
        image: dotPattern,
      });

      dotPatternImage = this.setDotColor(dotPatternImage, dotColorRGB);

      rectPattern.fillPatternImage(dotPatternImage.toCanvas());
      rectPattern.fillPatternScale({ x: 0.25, y: 0.25 });
    };
    dotPattern.src = "../../assets/dust-ex-zone-pattern.svg";

    const rectBg = new Rect({
      x: 0,
      y: 0,
      height: dustExZone.height,
      width: dustExZone.width,
      strokeEnabled: false,
      fill: color,
      opacity: 0.1,
    });

    const rectPattern = new Rect({
      x: 0,
      y: 0,
      height: dustExZone.height,
      width: dustExZone.width,
      strokeEnabled: false,
    });

    const borderRect = new Konva.Rect({
      x: 0,
      y: 0,
      width: dustExZone.width,
      height: dustExZone.height,
      stroke: "#002766",
      strokeWidth: 4,
      fillEnabled: false,
      opacity: 1,
    });

    const itemText = FloorplanItemTextKonva.init(dustExZone);
    const text = FloorplanItemTextKonva.getText(itemText);
    ZoneKonva.setTextPosition(itemText, dustExZoneGroup);
    ZoneKonva.scaleText(text, dustExZoneGroup);

    dustExZoneGroup.on("transformstart", () => {
      itemText.hide();
    });

    dustExZoneGroup.on("transform", () => {
      dustExZoneGroup.width(Math.max(5, dustExZoneGroup.width() * dustExZoneGroup.scaleX()));
      dustExZoneGroup.height(Math.max(5, dustExZoneGroup.height() * dustExZoneGroup.scaleY()));

      rectPattern.width(dustExZoneGroup.width());
      rectPattern.height(dustExZoneGroup.height());

      rectBg.width(dustExZoneGroup.width());
      rectBg.height(dustExZoneGroup.height());

      borderRect.width(dustExZoneGroup.width());
      borderRect.height(dustExZoneGroup.height());

      dustExZoneGroup.scaleX(1);
      dustExZoneGroup.scaleY(1);
    });

    dustExZoneGroup.on("transformend", () => {
      dustExZone.updateForm(
        dustExZoneGroup.width() * dustExZoneGroup.scaleX(),
        dustExZoneGroup.height() * dustExZoneGroup.scaleY(),
        dustExZoneGroup.rotation(),
        dustExZoneGroup.x(),
        dustExZoneGroup.y(),
      );
      ZoneKonva.scaleText(text, dustExZoneGroup);
      ZoneKonva.setTextPosition(itemText, dustExZoneGroup);
      const labelHidden = itemText.getAttr("labelHidden") === true;
      if (!labelHidden) {
        itemText.show();
      }
    });

    dustExZoneGroup.on("dragmove", () => {
      ZoneKonva.setTextPosition(itemText, dustExZoneGroup);
    });

    dustExZoneGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, dustExZone);
    dustExZoneGroup.add(rectPattern);
    dustExZoneGroup.add(rectBg);
    dustExZoneGroup.add(borderRect);

    parentGroup.add(dustExZoneGroup, itemText);

    return parentGroup;
  }

  static refreshSize(zone: DustExZone, zoneGroup: Group, width: number, height: number) {
    ZoneKonva.resizeZoneGroup(zoneGroup, width, height);
    this.refreshItemText(zone, zoneGroup);
  }

  static refreshItemText(dustExZone: DustExZone, group: Group) {
    const text = group.getChildren()[1] as Group;
    const dustExZoneGroup = group.getChildren()[0] as Konva.Group;
    dustExZoneGroup.width(dustExZone.width);
    dustExZoneGroup.height(dustExZone.height);
    FloorplanItemTextKonva.refreshText(dustExZone, text);
    const textNode = FloorplanItemTextKonva.getText(text);
    ZoneKonva.scaleText(textNode, dustExZoneGroup);
    ZoneKonva.setTextPosition(text, dustExZoneGroup);
  }

  private static setDotColor(image: Konva.Image, color: number[]): Konva.Image {
    image.cache();
    image.filters([Konva.Filters.RGBA]);
    image.red(color[0]);
    image.green(color[1]);
    image.blue(color[2]);
    image.alpha(0.3);

    //The filter only takes effect if the image is added to a layer and drawn
    const tempLayer = new Konva.Layer();
    tempLayer.add(image);
    tempLayer.draw();

    return image;
  }
}
